/** @jsx jsx */
import React, { useCallback, useState } from 'react';
import noop from 'lodash/noop';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner';
import EmptyState from '@atlaskit/empty-state';
import { LinkButton } from '@atlaskit/button/new';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ErrorFlag, FlagGroup } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import type {
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkV3Data,
	ApplicationLinkV3Status,
} from '../../common/entities.tsx';
import ProductCard from '../common/product-card/index.tsx';
import { lookupTypeByTypeId } from '../../common/application-types.tsx';
import { APPLINKS_LIST_PAGE_URL } from '../../common/constants.tsx';
import { ErrorStatusSectionMessage } from '../common/status/error/ErrorStatusSectionMessage.tsx';
import { ERRORS } from '../../common/status/errors.tsx';
import { StatusLozengeV3 } from '../common/status/lozenge/StatusLozenge.tsx';
import commonMessages from '../messages.tsx';
import statusMessages from '../common/status/messages.tsx';
import messages from './messages.tsx';
import ApplinksOAuth2EditPageContents from './oauth2/index.tsx';
import ApplinksOAuth1EditPageContents from './oauth1/index.tsx';

const styles = cssMap({
	applinksPageContents: {
		maxWidth: '900px',
		margin: '0 auto',
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
});

const DisplayError = (props: { header: string; description: string }) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.applinksPageContents}>
			<EmptyState
				header={props.header}
				description={props.description}
				primaryAction={
					<LinkButton href="/plugins/servlet/applinks/listApplicationLinks" appearance="primary">
						{formatMessage(messages.seeAllApplinksButton)}
					</LinkButton>
				}
			/>
		</Box>
	);
};

export type ApplinksErrorProps = {
	kind: 'ERROR';
	err: 'NOT_EDITABLE';
};

export type ApplinksOAuth1Props = {
	kind: 'OAUTH1';
	applink: ApplicationLinkV3Data;
};

export type ApplinksOAuth2Props = {
	kind: 'OAUTH2';
	applink: ApplicationLinkV3Data;
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo | null;
};

export type ApplinksProps = ApplinksErrorProps | ApplinksOAuth1Props | ApplinksOAuth2Props;

type ApplinksEditPageContentsProps = {
	isLoading?: boolean;
	data: ApplinksProps | null;
	status: ApplicationLinkV3Status | null;
	onRequestStatusRefresh: () => void;
};

const ApplinksEditPageContents = (props: ApplinksEditPageContentsProps) => {
	const [errorFlags, setErrorFlags] = useState<Array<string>>([]);

	const { formatMessage } = useIntl();

	const breadcrumbs = useCallback(
		() => (
			<Breadcrumbs onExpand={noop}>
				<BreadcrumbsItem
					text={formatMessage(commonMessages.applicationLinksTitle)}
					key="Application Links"
					href={APPLINKS_LIST_PAGE_URL}
				/>
			</Breadcrumbs>
		),
		[formatMessage],
	);

	const addFlag = useCallback(
		(err: string) => {
			const newFlags = errorFlags.slice();
			newFlags.splice(0, 0, err);

			setErrorFlags(newFlags);
		},
		[errorFlags],
	);

	const handleDismiss = () => {
		setErrorFlags(errorFlags.slice(1));
	};

	const renderStatusError = useCallback(() => {
		if (props.status && !props.status.working) {
			return (
				<ErrorStatusSectionMessage
					err={props.status.error ? ERRORS[props.status.error.type] : ERRORS.UNKNOWN}
					context={props.status}
				/>
			);
		}

		return <></>;
	}, [props.status]);

	const renderContents = useCallback(() => {
		if (props.isLoading) {
			return (
				<Inline alignInline="center" space="space.100" xcss={styles.applinksPageContents}>
					<Spinner />
				</Inline>
			);
		}
		if (props.data === null) {
			return (
				<DisplayError
					header={formatMessage(messages.applinkNotFoundHeader)}
					description={formatMessage(messages.applinkNotFoundDescription)}
				/>
			);
		}
		if (props.data.kind === 'ERROR') {
			switch (props.data.err) {
				// TODO: Add more error cases here
				case 'NOT_EDITABLE':
				default:
					return (
						<DisplayError
							header={formatMessage(messages.applinkNotEditableHeader)}
							description={formatMessage(statusMessages.systemLinkDescription)}
						/>
					);
			}
		}
		return (
			<Stack grow="fill" xcss={styles.applinksPageContents}>
				<PageHeader breadcrumbs={breadcrumbs()}>{formatMessage(messages.editMainTitle)}</PageHeader>
				<Stack space="space.200">
					<Text>
						{formatMessage(messages.securedByMessage, {
							authMethod: (
								<Text as="strong">
									{props.data.applink.data.isOAuth2 ? 'OAuth 2.0' : 'OAuth 1.0'}
								</Text>
							),
						})}
					</Text>
					<ProductCard
						applicationType={lookupTypeByTypeId(props.data.applink.type, false)}
						displayUrl={props.data.applink.displayUrl}
						heading={formatMessage(messages.productCardHeader)}
						statusLozenge={<StatusLozengeV3 status={props.status} />}
					/>
					{renderStatusError()}
				</Stack>
				{props.data.kind === 'OAUTH2' ? (
					<ApplinksOAuth2EditPageContents
						applink={props.data.applink}
						oauth2ConfigInfo={props.data.oauth2ConfigInfo}
						onDisplayError={addFlag}
						onRequestStatusRefresh={props.onRequestStatusRefresh}
					/>
				) : (
					<ApplinksOAuth1EditPageContents
						applink={props.data.applink}
						status={props.status}
						onDisplayError={addFlag}
					/>
				)}
			</Stack>
		);
	}, [
		addFlag,
		breadcrumbs,
		formatMessage,
		props.data,
		props.isLoading,
		props.onRequestStatusRefresh,
		props.status,
		renderStatusError,
	]);

	return (
		<>
			<JSErrorBoundary
				id="applinks-administration-edit-page"
				teamName="olympus"
				packageName="applinksAdministrationEditPage"
			>
				<ModalContextProvider>{renderContents()}</ModalContextProvider>
				<FlagGroup onDismissed={handleDismiss}>
					{errorFlags.map((err, idx) => {
						return (
							<ErrorFlag
								id={`flag-${idx}`}
								testId={`applinks-administration.ui.edit.error-flag-${idx}`}
								key={`flag-${idx}`}
								title={formatMessage(messages.errorFlagTitle)}
								description={err}
							/>
						);
					})}
				</FlagGroup>
			</JSErrorBoundary>
		</>
	);
};

export default ApplinksEditPageContents;
